import { createContext, useContext } from 'react';

/**
 * Subscribe to a platform EventBus broadcast by name (架构方案 §5.2 listen /
 * §5.15), returning a disposer. This is the view-facing shape of
 * `ChannelClient.listen`: the general publish/subscribe counterpart of the
 * request/response `executeCommand`, so a source-module renderer view can react
 * to broadcasts it (or another module) raised via `context.emitEvent`.
 */
export type ViewEventSubscribe = (
  event: string,
  handler: (data: unknown) => void,
) => () => void;

/**
 * Ambient EventBus subscription for inline-dom views (架构方案 §5.8 / §5.15),
 * mirroring {@link NavigationContext}. The app-shell-facing hosts
 * (`Workbench` / `DetachedSurface`) provide a `client.listen`-backed subscriber
 * bound to the live channel; `SurfaceView` reads it to build `ViewProps.onEvent`.
 * Kept in context (not prop-drilled) so the many `SurfaceView` render sites stay
 * unchanged and re-bind automatically when the channel reconnects. The default
 * is a no-op subscription (headless / detached-without-client): the view simply
 * never receives events rather than throwing.
 */
export const ViewEventBusContext = createContext<ViewEventSubscribe>(
  () => () => undefined,
);

/**
 * The ambient {@link ViewEventSubscribe} for the current surface (架构方案 §5.15).
 * Returns a no-op subscriber when no provider is present, so views degrade
 * gracefully in headless / detached-without-client contexts.
 */
export function useViewEventBus(): ViewEventSubscribe {
  return useContext(ViewEventBusContext);
}
